/**
 * The four states, adapted once so no screen in this feature hand-rolls one.
 *
 * `LoadingState` and `ErrorState` come from `@moonx/ui` — they are two of the three "states,
 * standardised" in the 39-component inventory (docs/ui/03-components.md) and they already
 * carry the rules that matter: `role="alert"` on a failure, a polite live region behind a skeleton
 * that a screen reader cannot otherwise perceive, a retry that is wired rather than drawn, and the
 * ban on surfacing a raw error code or a stack trace to an author.
 *
 * `EmptyState` is deliberately NOT in that barrel and is imported straight from `@mantine/core`:
 * Mantine 9 ships one and the inventory says in terms "do not hand-roll it".
 *
 * WHAT THIS FILE ADDS: nothing but names and defaults. It exists so the feature's screens read the
 * same whichever state they are in, and so that swapping the underlying component later is one
 * file. If it ever starts holding behaviour, the behaviour belongs in `web/ui`.
 */
import { EmptyState } from '@mantine/core';
import { ErrorState, LoadingState } from '@moonx/ui';
import type { ReactNode } from 'react';

export interface LoadingBlockProps {
  /**
   * A NOUN PHRASE, not a sentence: `LoadingState` announces it as "Loading {label}." So
   * "the current terms", never "Checking the current terms…".
   */
  readonly label: string;
  readonly lines?: number;
  readonly height?: number;
}

export function LoadingBlock({ label, lines = 3, height = 14 }: LoadingBlockProps) {
  return <LoadingState label={label} lines={lines} height={height} />;
}

export interface FailureBlockProps {
  readonly title: string;
  readonly body: ReactNode;
  /** Omit for a failure that retrying cannot fix — a 403 is not a button. */
  readonly onRetry?: () => void;
  readonly retryLabel?: string;
  readonly retrying?: boolean;
}

export function FailureBlock({ title, body, onRetry, retryLabel, retrying }: FailureBlockProps) {
  return (
    <ErrorState
      title={title}
      // `exactOptionalPropertyTypes` is on, so an explicit `undefined` is not the same as an
      // omission — spread the optional half in only when there is something to say.
      {...(onRetry === undefined ? {} : { onRetry })}
      {...(retryLabel === undefined ? {} : { retryLabel })}
      {...(retrying === undefined ? {} : { retrying })}
    >
      {body}
    </ErrorState>
  );
}

export interface AbsenceBlockProps {
  readonly title: string;
  readonly body: string;
}

/**
 * The state that says WHY it is empty. "An empty state that says nothing is a bug, not a blank."
 *
 * `order` is left unset so Mantine renders the title as a `div` rather than a heading — otherwise
 * an empty state drops an `h?` into the middle of the page's heading outline.
 */
export function AbsenceBlock({ title, body }: AbsenceBlockProps) {
  return <EmptyState align="left" size="sm" title={title} description={body} />;
}
